import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, tokenStore } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      // Hydrate localStorage Bearer token if /auth/me echoed it back (this
      // happens when the user was cookie-authed and lacks the token locally —
      // e.g., they logged in BEFORE the Bearer-fallback fix shipped). This
      // single line is what prevents the 6th login loop when the cookie
      // eventually gets dropped by the platform proxy.
      if (data?.session_token) tokenStore.set(data.session_token);
      setUser(data);
    } catch {
      setUser(null);
      tokenStore.clear();
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      void err;
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (err) { void err; }
    tokenStore.clear();
    setUser(null);
    window.location.href = "/login";
  }, []);

  // Memoize value so consumers (Layout, Protected, etc.) don't re-render
  // every time AuthProvider re-renders for unrelated state changes.
  const value = useMemo(
    () => ({ user, setUser, loading, checkAuth, refreshUser, logout }),
    [user, loading, checkAuth, refreshUser, logout],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
